import { api, LightningElement } from "lwc";
import debounce from "lodash/debounce";
import { getClient } from "@devforce/trailhead-client-api";
import { getJSON } from "@devforce/trailhead-client-fetch";
import { logger } from "@devforce/trailhead-client-logger";

import {
  Completion,
  Recent,
  RecentSection,
  SearchEvent,
} from "../../tds/search/types";

import { RECENTS_PATH, TOKEN_PATH } from "./constants";
import { CoveoCompletion, Config, UserRecent } from "./types";

export default class extends LightningElement {
  @api config: Config = null!;

  private accessToken: string | null = null;

  private client = getClient();

  private completionTerms: string = "";
  private completionController: AbortController | null = null;
  private completions: Completion[] = [];

  private defaultCompletionTerms = "";

  private getCompletions = debounce(async (terms: string) => {
    let { completionController: prevController, config } = this;
    if (prevController) prevController.abort();
    if (!config) return undefined;
    if (!terms.length) {
      this.completions = [];
      return undefined;
    }
    let controller = (this.completionController = new AbortController());
    let accessToken = await this.getAccessToken();
    let url = getCompletionsUrl({
      accessToken,
      baseUrl: config.coveo.querySuggestUrl,
      locale: config.coveo.locale,
      terms,
    });
    let { completions } = await getJSON<{ completions: CoveoCompletion[] }>(
      url,
      {
        signal: controller.signal,
      }
    );
    if (this.completionTerms === terms)
      this.completions = completions.map((c) => ({
        value: c.expression,
        valueHighlighted: c.highlighted,
      }));
    return undefined;
  }, 250);

  private recents: RecentSection[] | null = null;

  private async getAccessToken() {
    if (this.accessToken) return this.accessToken;
    let { token } = await this.client.post<{ token: string }>(TOKEN_PATH);
    this.accessToken = token;
    return token;
  }

  private async getRecents() {
    let { config } = this;
    try {
      let { recents } = await this.client.get<{ recents: UserRecent[] }>(
        RECENTS_PATH
      );
      let { recent_favorite, recent_item } = recents.reduce<
        {
          [type in UserRecent["type"]]: UserRecent[];
        }
      >(
        (a, b) => {
          a[b.type].push(b);
          return a;
        },
        {
          recent_favorite: [],
          recent_item: [],
        }
      );
      this.recents = [
        {
          items: recent_item.map(toRecent),
          title: config.labels.recentViewed,
        },
        {
          items: recent_favorite.map(toRecent),
          title: config.labels.recentFavorites,
        },
      ].filter((s) => s.items.length);
    } catch (e) {
      console.error(e);
    }
  }

  private onFocus() {
    if (!this.recents)
      this.getRecents().catch((e: Error) => logger.warn(e.message));
  }

  private onChange(e: CustomEvent<{ value: string }>) {
    this.completionTerms = e.detail.value;
    this.getCompletions(e.detail.value)?.catch((e: Error) =>
      logger.warn(e.message)
    );
  }

  private onSearch(e: CustomEvent<SearchEvent>) {
    let { config } = this;
    switch (e.detail.type) {
      case "completion": {
        let query = new URLSearchParams({
          keywords: e.detail.value.value,
          suggested: "true",
        }).toString();
        window.location.href = `${config.searchUrl}?${query}`;
        break;
      }
      case "input": {
        let query = new URLSearchParams({
          keywords: e.detail.value,
        }).toString();
        window.location.href = `${config.searchUrl}?${query}`;
        break;
      }
      case "recent": {
        window.location.href = e.detail.value.path;
        break;
      }
      default:
        break;
    }
  }

  connectedCallback() {
    let params = new URLSearchParams(window.location.search);
    if (params.has("keywords")) {
      this.defaultCompletionTerms = params.get("keywords")!;
    }
  }
}

function getCompletionsUrl(params: {
  accessToken: string;
  baseUrl: string;
  locale: string;
  terms: string;
}) {
  let { accessToken, baseUrl, locale, terms } = { ...params };
  let query = new URLSearchParams({
    access_token: accessToken,
    count: "10",
    enableWordCompletion: "true",
    locale,
    q: terms,
  }).toString();
  return `${baseUrl}?${query}`;
}

function toRecent(recent: UserRecent): Recent {
  return {
    icon: recent.icon_url,
    iconBackgroundColor: recent.color ? recent.color : undefined,
    path: recent.path,
    pretitle: recent.content_type,
    title: recent.title,
  };
}
